import {
    DeleteEmployeeParams,
    EmployeeStatus,
    ErrorCode,
    Role,
    type UpdateEmployeeParams,
    employeeStatusColor,
    employeeStatusLabel,
    formatDate,
} from "@pentacode/core";
import type { MenuSelectEvent } from "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import "../../../components/avatar/avatar";
import { alert, confirm } from "../../../elements/alert-dialog";
import { Checkbox } from "../../../elements/checkbox";
import { DateInput } from "../../../elements/date-input";
import type { EmployeeList } from "../../../elements/employee-list";
import "../../../elements/scroller";
import "../../../elements/send-message-dialog";
import type { SendMessageDialog } from "../../../elements/send-message-dialog";
import { app } from "../../../init";
import { canAccess, menuData } from "../../../lib/route-tree";
import { singleton } from "../../../lib/singleton";
import { Routing, routeProperty } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { mixins, shared } from "../../../styles";
import { type EmployeePage, employeePages, employeeRoute } from "../../app-routes";
import { routeAccess } from "../../route-access";
import { getEmployeeRangeSlot, getRememberedEmployeeRange, rememberEmployeeRange } from "../shared-range-memory";
import "./components/retire-employee-dialog";
import type { RetireEmployeeDialog } from "./components/retire-employee-dialog";
import "./employees-absences-single";
import "./employees-advances-single";
import "./employees-availabilities-single";
import "./employees-bonuses-balance-single";
import "./employees-contracts-single";
import "./employees-core-data-single";
import "./employees-documents-single";
import "./employees-ledgers-single";
import "./employees-payroll-data-single";
import "./employees-permissions-single";
import "./employees-positions-single";
import "./employees-time-balance-single";
import "./employees-time-single";
import "./employees-vacation-balance-single";

@customElement("ptc-employees-single")
export class EmployeesSingle extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/(?<id>\d+)(?:\/(?<page>[^/]+))?/;

    @routeProperty({ type: Number, arg: "id" })
    private _employeeId: number;

    @routeProperty({ arg: "page" })
    private _page: string;

    @query("ptc-employee-list")
    private _employeeList: EmployeeList;

    @state()
    private _loading: boolean = false;

    @state()
    private _selectingEmployee: boolean = false;

    @singleton("ptc-send-message-dialog")
    private _sendMessageDialog: SendMessageDialog;

    @singleton("ptc-retire-employee-dialog")
    private _retireEmployeeDialog: RetireEmployeeDialog;

    private get _employee() {
        return app.getEmployee(this._employeeId)!;
    }
    private get _hasPermission() {
        const employee = this._employee;
        return app.hasPermissionForEmployee(employee);
    }

    private _isPage(page: string): page is EmployeePage {
        return (employeePages as string[]).includes(page);
    }

    private _canAccessPage(page: EmployeePage) {
        return canAccess(employeeRoute(page), routeAccess(this._employee));
    }

    async handleRoute() {
        const page = this._page;

        if (!this._hasPermission) {
            if (this._page !== "unauthorized") {
                this.go(`employees/${this._employeeId}/unauthorized`);
            }
            return;
        } else if (!this._isPage(page) || !this._canAccessPage(page)) {
            const page = ["core-data" as const, ...employeePages].find((page) => this._canAccessPage(page));
            if (page) {
                this.go(page ? `employees/${this._employeeId}/${page}` : "", undefined, true);
            } else {
                this._page = "";
            }
            return;
        }
    }

    async updated(changes: Map<string, unknown>) {
        if (!changes.has("_employeeId") && !changes.has("_page")) {
            return;
        }
    }

    private async _startSelectEmployee(e: MouseEvent) {
        e.stopPropagation();
        this._selectingEmployee = true;
        await this.updateComplete;
        this._employeeList.clearFilter();
        this._employeeList.focus();
    }

    private _employeeSelected(e: CustomEvent) {
        const slot = getEmployeeRangeSlot("single", this._page);
        if (slot) {
            const { from, to, date } = this.router.params;
            rememberEmployeeRange(slot, { from, to, date });
        }

        // Keep the shared month when switching employees; history pages fall back
        // to the newly selected employee's own default (their entire history).
        const remembered = slot === "month" ? getRememberedEmployeeRange("month") : undefined;
        this.go(`employees/${e.detail.employee.id}/${this._page}`, remembered);
        this._selectingEmployee = false;
    }

    private async _updateAvatar({ detail: { image } }: CustomEvent) {
        await this._update({ avatar: image });
    }

    private async _update(data: Partial<UpdateEmployeeParams>, rethrowErrors = false, refresh = true) {
        this._loading = true;
        try {
            await app.updateEmployee(this._employee!, data, refresh);
            this._loading = false;
        } catch (e) {
            this._loading = false;
            if (rethrowErrors) {
                throw e;
            } else {
                void alert(e.message, { type: "warning" });
            }
        }

        void this.updated(new Map<string, unknown>([["_employeeId", this._employeeId]]));
    }

    private async _deleteEmployee() {
        if (
            !(await confirm(
                "Sind Sie sicher dass Sie diesen Mitarbeiter löschen möchten? Diese Änderung kann nicht rückgängig gemacht werden!",
                "Löschen",
                "Abbrechen",
                {
                    title: "Mitarbeiter Löschen",
                    type: "destructive",
                }
            ))
        ) {
            return;
        }

        this._loading = true;

        try {
            await app.api.deleteEmployee(new DeleteEmployeeParams({ id: this._employee.id }));
        } catch (e) {
            if (e.code === ErrorCode.CONFLICT) {
                const confirmed = await confirm(e.message, "Fortfahren", "Abbrechen", {
                    type: "destructive",
                });

                if (!confirmed) {
                    this._loading = false;
                    return;
                }

                await app.api.deleteEmployee(
                    new DeleteEmployeeParams({ id: this._employee.id, deleteDanglingEntries: true })
                );
            } else {
                void alert(e.message, { type: "warning" });
                this._loading = false;
                return;
            }
        }

        this._loading = false;

        // Refresh company data in the background
        void app.fetchCompany();

        this.go("employees/all");
    }

    private _goToPage(page: string, params?: { [param: string]: string | number }) {
        const currentSlot = getEmployeeRangeSlot("single", this._page);
        if (currentSlot) {
            const { from, to, date } = this.router.params;
            rememberEmployeeRange(currentSlot, { from, to, date });
        }

        const targetSlot = getEmployeeRangeSlot("single", page);
        const remembered = targetSlot ? getRememberedEmployeeRange(targetSlot) : undefined;
        this.go(`employees/${this._employeeId}/${page}`, { ...remembered, ...params });
    }

    private async _hireAsFullEmployee() {
        if (
            !(await confirm(
                "Möchten Sie diesen Probemitarbeiter als vollen Mitarbeiter einstellen?",
                "Einstellen",
                "Abbrechen",
                {
                    icon: "icon.action.hire",
                    title: "Mitarbeiter Einstellen",
                }
            ))
        ) {
            return;
        }

        this._loading = true;

        try {
            await app.updateEmployee(this._employee, { status: EmployeeStatus.Active });
            this._goToPage(`contracts/${this._employee.contracts[0]?.id || "new"}`);
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _retireEmployee() {
        void this._retireEmployeeDialog.show(this._employee);
    }

    private async _rehireEmployee() {
        if (
            !(await confirm("Möchten Sie diesen Mitarbeiter wiedereinstellen?", "Wiedereinstellen", "Abbrechen", {
                icon: "icon.action.rehire",
                title: "Mitarbeiter Wiedereinstellen",
            }))
        ) {
            return;
        }

        this._loading = true;

        try {
            await app.updateEmployee(this._employee, { status: EmployeeStatus.Active, scheduledStatusChange: null });
            this._goToPage(`contracts/new`);
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _documentClickHandler = () => (this._selectingEmployee = false);

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener("click", this._documentClickHandler);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener("click", this._documentClickHandler);
    }

    static styles = [
        shared,
        Checkbox.styles,
        DateInput.styles,
        css`
            :host {
                display: flex;
            }

            .sub-menu {
                width: 15em;
                padding: 0;
                position: relative;
            }

            .employee-list {
                ${mixins.fullbleed()};
                background: var(--color-bg);
                z-index: 1;
            }

            .employee-avatar {
                font-size: var(--font-size-enormous);
                align-self: center;
            }

            .employee-name {
                font-size: var(--font-size-medium);
                align-self: center;
                margin: 0.5em 0;
                padding: 0.3em;
                padding-left: 0.5em;
            }

            .employee-positions {
                margin-bottom: 0;
            }

            .sub-view {
                flex: 1;
                min-width: 0;
                position: relative;
                border-right: solid 1px var(--shade-1);
                margin-right: -1px;
            }

            @media print {
                .sub-view {
                    max-width: none;
                    border: none;
                }
            }
        `,
    ];

    render() {
        return html`
            <div class="sub-menu noprint vertical layout">${this._renderSubMenuHeader()} ${this._renderSubMenu()}</div>
            <div class="sub-view">
                ${this._renderRoute(this._page)}

                <div class="fullbleed centering vertical layout" ?hidden=${this._page !== "unauthorized"}>
                    <ptc-ui-icon name="icon.status.access-denied" class="giant subtle"></ptc-ui-icon>
                    <div class="subtle">Dieser Mitarbeiter liegt außerhalb Ihrer Berechtigungen.</div>
                </div>
                <div
                    class="fullbleed center-aligning center-justifying vertical layout scrim"
                    ?hidden=${!this._loading}
                >
                    <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                </div>
            </div>
        `;
    }

    private _routeMap = new Map([
        ["time", () => html`<ptc-employees-time-single class="fullbleed"></ptc-employees-time-single>`],
        ["ledgers", () => html`<ptc-employees-ledgers-single class="fullbleed"></ptc-employees-ledgers-single>`],
        [
            "time-balance",
            () => html`<ptc-employees-time-balance-single class="fullbleed"></ptc-employees-time-balance-single>`,
        ],
        [
            "vacation-balance",
            () =>
                html`<ptc-employees-vacation-balance-single class="fullbleed"></ptc-employees-vacation-balance-single>`,
        ],
        [
            "bonuses-balance",
            () => html`<ptc-employees-bonuses-balance-single class="fullbleed"></ptc-employees-bonuses-balance-single>`,
        ],
        ["absences", () => html`<ptc-employees-absences-single class="fullbleed"></ptc-employees-absences-single>`],
        [
            "availabilities",
            () => html`<ptc-employees-availabilities-single class="fullbleed"></ptc-employees-availabilities-single>`,
        ],
        ["documents", () => html`<ptc-employees-documents-single class="fullbleed"></ptc-employees-documents-single>`],
        ["advances", () => html`<ptc-employees-advances-single class="fullbleed"></ptc-employees-advances-single>`],
        ["core-data", () => html`<ptc-employees-core-data-single class="fullbleed"></ptc-employees-core-data-single>`],
        [
            "payroll-data",
            () => html`<ptc-employees-payroll-data-single class="fullbleed"></ptc-employees-payroll-data-single>`,
        ],
        ["contracts", () => html`<ptc-employees-contracts-single class="fullbleed"></ptc-employees-contracts-single>`],
        [
            "permissions",
            () => html`<ptc-employees-permissions-single class="fullbleed"></ptc-employees-permissions-single>`,
        ],
        ["positions", () => html`<ptc-employees-positions-single class="fullbleed"></ptc-employees-positions-single> `],
    ]);

    private _renderRoute(route: string) {
        const renderer = this._routeMap.get(route);
        return renderer ? renderer() : nothing;
    }

    private _renderSubMenuHeader() {
        const emp = this._employee;

        if (!emp) {
            return html``;
        }

        const hasPermission = this._hasPermission;

        return html`<ptc-employee-list
                .employee=${this._employee}
                @change=${this._employeeSelected}
                @navigation-changed=${() => this.requestUpdate()}
                class="employee-list"
                ?hidden=${!this._selectingEmployee}
            >
            </ptc-employee-list>

            <div class="horizontally-margined vertically-padded vertical text-centering layout border-bottom">
                <div class="horizontal layout">
                    <button
                        class="transparent skinny"
                        @click=${() => {
                            const currentSlot = getEmployeeRangeSlot("single", this._page);
                            if (currentSlot) {
                                const { from, to, date } = this.router.params;
                                rememberEmployeeRange(currentSlot, { from, to, date });
                            }

                            // The "all employees" overview always works on the shared month.
                            const remembered = getEmployeeRangeSlot("all", this._page)
                                ? getRememberedEmployeeRange("month")
                                : undefined;
                            this.go(`employees/all/${this._page}`, remembered);
                        }}
                        title="Alle Mitarbeiter"
                    >
                        <ptc-ui-icon name="icon.navigation.back"></ptc-ui-icon>
                        <ptc-ui-icon name="icon.object.employee-group"></ptc-ui-icon>
                    </button>

                    <div class="stretch"></div>

                    <button
                        class="transparent skinny"
                        @click=${() => this._employeeList.selectPrevious()}
                        ?disabled=${!this._employeeList?.hasPrevious}
                        title="Vorheriger Mitarbeiter"
                    >
                        <ptc-ui-icon name="icon.navigation.up"></ptc-ui-icon>
                    </button>

                    <button
                        class="transparent skinny"
                        @click=${() => this._employeeList.selectNext()}
                        ?disabled=${!this._employeeList?.hasNext}
                        title="Nächster Mitarbeiter"
                    >
                        <ptc-ui-icon name="icon.toggle.expand-active"></ptc-ui-icon>
                    </button>

                    <div class="stretch"></div>

                    <button id="employeeMenuButton" data-testid="employees-ellipsis-button" class="transparent skinny">
                        <ptc-ui-icon name="icon.action.show-more"></ptc-ui-icon>
                    </button>

                    <ptc-ui-popover
                        for="employeeMenuButton"
                        placement="bottom"
                        class="popover-menu"
                        data-popover="close"
                    >
                        ${emp.status !== EmployeeStatus.Probation
                            ? html`
                                  <button
                                      class="transparent"
                                      @click=${() =>
                                          this.go("exports/employee-data-pdf", {
                                              "entity-filters": JSON.stringify([
                                                  { type: "employeeId", value: this._employeeId },
                                              ]),
                                          })}
                                      ?disabled=${!hasPermission || !app.hasPermission("manage.employees.contract")}
                                  >
                                      <ptc-ui-icon name="icon.object.master-data"></ptc-ui-icon> Datenblatt Drucken
                                  </button>

                                  <button
                                      class="transparent"
                                      @click=${() => this._sendMessageDialog.show({ employees: [this._employee] })}
                                      ?disabled=${!emp.email || !app.hasPermission("manage.employees.messages")}
                                  >
                                      <ptc-ui-icon name="icon.action.send-message"></ptc-ui-icon> Nachricht Senden
                                  </button>
                              `
                            : ""}
                        ${emp.status === EmployeeStatus.Retired ||
                        emp.scheduledStatusChange?.status === EmployeeStatus.Retired
                            ? html`
                                  <button
                                      class="transparent"
                                      @click=${() => this._rehireEmployee()}
                                      ?disabled=${!hasPermission || !app.hasPermission("manage.employees.contract")}
                                  >
                                      <ptc-ui-icon name="icon.action.rehire"></ptc-ui-icon> Wiedereinstellen
                                  </button>
                              `
                            : emp.status === EmployeeStatus.Probation
                              ? html`
                                    <button
                                        class="transparent"
                                        @click=${() => this._hireAsFullEmployee()}
                                        ?disabled=${!hasPermission || !app.hasPermission("manage.employees.contract")}
                                    >
                                        <ptc-ui-icon name="icon.action.hire"></ptc-ui-icon> Einstellen
                                    </button>
                                `
                              : emp.status === EmployeeStatus.Active
                                ? html`
                                      <button
                                          class="transparent"
                                          @click=${() => this._retireEmployee()}
                                          ?disabled=${emp.role === Role.Owner ||
                                          !hasPermission ||
                                          !app.hasPermission("manage.employees.contract")}
                                      >
                                          <ptc-ui-icon name="icon.action.terminate"></ptc-ui-icon> Entlassen
                                      </button>
                                  `
                                : ""}

                        <button
                            data-testid="employees-delete-button"
                            class="transparent"
                            @click=${() => this._deleteEmployee()}
                            ?disabled=${emp.role === Role.Owner ||
                            !hasPermission ||
                            !app.hasPermission("manage.employees.delete")}
                        >
                            <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon> Mitarbeiter Löschen
                        </button>
                    </ptc-ui-popover>
                </div>

                <ptc-avatar
                    .employee=${emp}
                    class="employee-avatar"
                    @edit=${this._updateAvatar}
                    .editable=${hasPermission && app.hasPermission("manage.employees.core_data")}
                ></ptc-avatar>

                <button
                    class="center-aligning center-justifying horizontal layout employee-name transparent"
                    @click=${this._startSelectEmployee}
                >
                    <div class="stretch">${emp.name}</div>
                    <ptc-ui-icon name="icon.navigation.dropdown"></ptc-ui-icon>
                </button>

                <div class="smaller employee-positions pills">
                    ${emp.status !== EmployeeStatus.Active
                        ? html`
                              <div class="${employeeStatusColor(emp.status)} inverted pill">
                                  ${employeeStatusLabel(emp.status)}
                              </div>
                          `
                        : ""}
                    ${emp.scheduledStatusChange
                        ? html`
                              <div class="${employeeStatusColor(emp.scheduledStatusChange.status)} inverted pill">
                                  ${employeeStatusLabel(emp.scheduledStatusChange.status)} ab
                                  ${formatDate(emp.scheduledStatusChange.date)}
                              </div>
                          `
                        : ""}
                    ${emp.positions
                        .slice(0, 2)
                        .map(
                            (position) => html`
                                <div class="pill" style="--color-highlight: ${app.getPositionColor(position)}">
                                    ${position.name}
                                </div>
                            `
                        )}
                    ${emp.positions.length > 2
                        ? html`
                              <div class="pill" id="morePositions">+ ${emp.positions.length - 2}</div>
                              <ptc-ui-tooltip for="morePositions">
                                  <div class="smaller vertical spacing layout text-left-aligning">
                                      ${emp.positions
                                          .slice(2)
                                          .map(
                                              (position) => html`
                                                  <div
                                                      class="pill"
                                                      style="--color-highlight: ${app.getPositionColor(position)}"
                                                  >
                                                      ${position.name}
                                                  </div>
                                              `
                                          )}
                                  </div>
                              </ptc-ui-tooltip>
                          `
                        : ""}
                </div>

                ${emp.status === EmployeeStatus.Probation
                    ? html`
                          <button
                              class="margined subtle"
                              @click=${() => this._hireAsFullEmployee()}
                              ?disabled=${!hasPermission || !app.hasPermission("manage.employees.contract")}
                          >
                              <ptc-ui-icon name="icon.action.hire"></ptc-ui-icon> Einstellen
                          </button>
                      `
                    : emp.status === EmployeeStatus.Retired
                      ? html`
                            <button
                                class="margined subtle"
                                @click=${() => this._rehireEmployee()}
                                ?disabled=${!hasPermission || !app.hasPermission("manage.employees.contract")}
                            >
                                <ptc-ui-icon name="icon.action.rehire"></ptc-ui-icon> Wiedereinstellen
                            </button>
                        `
                      : ""}
            </div> `;
    }

    private _renderSubMenu() {
        const emp = this._employee;

        if (!emp) return nothing;

        return html`
            <ptc-scroller class="stretch">
                <ptc-ui-menu
                    class="horizontally-margined"
                    dividers
                    .groups=${menuData(employeePages, employeeRoute, routeAccess(this._employee))}
                    active=${this._page}
                    @menu-select=${(e: MenuSelectEvent) => this._goToPage(e.detail.id)}
                    ><slot name="submenu" slot="replace"></slot
                ></ptc-ui-menu>

                <div class="horizontally-margined vertically-padded vertical tabs">
                    <button
                        @click=${() => this.dispatchEvent(new CustomEvent("newemployee"))}
                        ?disabled=${!this._hasPermission || !app.hasPermission("manage.employees.create")}
                    >
                        <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                        Neuer Mitarbeiter
                    </button>
                </div>
            </ptc-scroller>
        `;
    }
}
